import { CommonModule } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { NavigationEnd, Router, RouterModule } from '@angular/router'
import { ReportNavService } from '@employer/app/services/report-nav.service'
import * as fromReducers from '@employer/app/store/reducers'
import { selectors } from '@employer/app/store/selectors'
import { ICustomerDepartment, ICustomerLocation } from '@engineering11/customer-web'
import { E11DividerModule } from '@engineering11/ui-lib/e11-divider'
import { IMultiSelectOption, mapToMultiselectOptions } from '@engineering11/ui-lib/e11-multi-select'
import { LetDirective } from '@ngrx/component'
import { Store } from '@ngrx/store'
import { Observable, filter, map, of, startWith } from 'rxjs'
import { CareersPageFilteringComponent } from './partials/careers-page-filtering.component'
import { ReportFilterFormComponent } from './partials/report-filtering-form.component'
import { SectionNavigationComponent } from './partials/section-navigation.component'
import { ISummaryCard, summaryList } from './report-filtering.model'
import { SingleJobFilteringComponent } from './single-job-filtering.component'
export interface IReportFilteringForm {
  startDate: string
  endDate: string
  dateType: string
  jobStatus: string[]
  locationAddress: string[]
  city: string[]
  department: string[]
  employmentType: string[]
  experienceLevel: string[]
  employeeLocation: string[]
}
export interface IReportTab {
  id: string
  label: string
}
@Component({
  selector: 'report-filtering-nav',
  standalone: true,
  imports: [
    CommonModule,
    E11DividerModule,
    SectionNavigationComponent,
    ReportFilterFormComponent,
    RouterModule,
    SingleJobFilteringComponent,
    CareersPageFilteringComponent,
    LetDirective,
  ],
  // The rail mixes three ui-lib field types — a native select, the date picker and the
  // multi-select — and each ships its own height, border, corners and text colours (the date
  // picker was overridden to brand blue; the multi-select uses raw Tailwind greys). These rules
  // give all three the select's theme look: app-borders outline, theme text colours, one height.
  // ui-lib renders the date picker and multi-select markup itself, so they are reached via
  // ::ng-deep, scoped to this rail. Values come from the field variables in
  // styles/_modern-components.scss (--mc-field-*), so a theme-wide field change reaches the rail.
  styles: [
    `
      :host {
        --report-field-height: var(--mc-field-height);
      }
      :host ::ng-deep sdk-report-filtering select,
      :host ::ng-deep e11-date-picker .e11-relative > div.e11-border,
      :host ::ng-deep e11-multi-select button.e11-border {
        min-height: var(--report-field-height);
        border-radius: var(--mc-field-radius);
        background-color: var(--mc-field-bg);
        font-size: var(--mc-field-font-size);
        color: var(--mc-field-text);
      }
      :host ::ng-deep sdk-report-filtering select {
        padding-left: var(--mc-field-padding-x);
      }

      /* Date picker: full width like its neighbours, value in theme text (not brand blue), and
         the calendar icon in the trailing slot where the others show their chevron. */
      :host ::ng-deep e11-date-picker .e11-relative > div.e11-border {
        position: relative;
        width: 100%;
        padding: 0;
      }
      /* The 16px below each date field sits on the picker itself, not on its box: the calendar
         opens straight after the box, so a margin there left a gap above it. */
      :host ::ng-deep e11-date-picker {
        display: block;
        margin-bottom: 1rem;
      }
      :host ::ng-deep e11-date-picker .e11-datepicker {
        margin-top: 4px;
      }
      /* ui-lib only opens the calendar from this text box, so it spans the whole field and
         the icon sits over it with clicks passing through — the icon opens it too. */
      :host ::ng-deep e11-date-picker .e11-relative > div.e11-border > div {
        flex: 1;
        width: auto;
        margin: 0;
        padding: 0 2.5rem 0 var(--mc-field-padding-x);
        line-height: calc(var(--report-field-height) - 2px);
        color: var(--mc-field-text);
      }
      :host ::ng-deep e11-date-picker .e11-relative > div.e11-border > div.e11-date-picker-placeholder {
        color: var(--mc-field-placeholder);
      }
      :host ::ng-deep e11-date-picker .e11-relative > div.e11-border > .material-icons-outlined {
        position: absolute;
        right: var(--mc-field-padding-x);
        margin: 0;
        font-size: 20px;
        color: var(--mc-field-icon);
        pointer-events: none;
      }

      /* Multi-select: the chevron in the theme icon colour instead of gray-500 (ui-lib colours the
         placeholder itself). */
      :host ::ng-deep e11-multi-select button.e11-border {
        padding-left: var(--mc-field-padding-x);
      }
      :host ::ng-deep e11-multi-select button.e11-border .e11-text-gray-500 {
        color: var(--mc-field-icon);
      }
    `,
  ],
  template: `
    <div class="e11-pr-2 e11-py-4">
      <section-navigation (scrollTo)="scrollToSection($event)"></section-navigation>
      <e11-divider [margin]="1" />
      <ng-container *ngrxLet="careersPageRoute$ as careersPageRoute">
        @if (careersPageRoute) {
        <careers-page-filtering></careers-page-filtering>
        } @else {
        <report-filter-form
          [isSingleJobPostRoute]="(isSingleJobPostRoute$ | async) ?? false"
          [cityOptions]="(listOfCities$ | async) ?? []"
          [location]="(listOfLocations$ | async) ?? []"
          [department]="(listOfDepartments$ | async) ?? []"
          [loading]="(loading$ | async) ?? false"
          [selectedChartName]="(selectedChartName$ | async) ?? undefined"
        ></report-filter-form>
        <single-job-filtering
          [isSingleJobPostRoute]="(isSingleJobPostRoute$ | async) ?? false"
          (scrollTo)="scrollToSection($event)"
        ></single-job-filtering>
        }
      </ng-container>
    </div>
  `,
})
export class ReportFilteringNavComponent implements OnInit {
  listOfDepartments$?: Observable<IMultiSelectOption[]>
  listOfLocations$?: Observable<IMultiSelectOption[]>
  listOfCities$?: Observable<IMultiSelectOption[]>

  summaryList: ISummaryCard[] = summaryList
  loading$: Observable<boolean> = this.store.select(selectors.loading)
  isSingleJobPostRoute$: Observable<boolean> = of(false)
  careersPageRoute$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.currentUrl().includes('reports/careers-page'))
  )
  selectedChartName$ = this.reportNavService.selectedChartName$
  constructor(private store: Store<fromReducers.customer.State>, private router: Router, private reportNavService: ReportNavService) {}

  ngOnInit(): void {
    this.isSingleJobPostRoute$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.currentUrl().includes('reports/job-posts/'))
    )

    this.loading$ = this.store.select(selectors.loading)

    this.getLocation()
    this.getCities()
    this.getDepartment()
  }
  /** The rail mounts at NavigationStart, so the URL being navigated to wins over the one being left. */
  private currentUrl(): string {
    const navigation = this.router.getCurrentNavigation?.()
    return (navigation?.finalUrl ?? navigation?.extractedUrl)?.toString() ?? this.router.url
  }

  getLocation() {
    this.listOfLocations$ = this.store.select(selectors.getCustomerLocationEntities).pipe(
      map((locations: ICustomerLocation[]) => {
        return mapToMultiselectOptions(
          locations,
          l => l.name,
          l => l.name
        )
      })
    )
  }
  getDepartment() {
    this.listOfDepartments$ = this.store.select(selectors.getCustomerDepartmentEntities).pipe(
      map((departments: ICustomerDepartment[]) =>
        mapToMultiselectOptions(
          departments,
          d => d.name,
          d => d.name
        )
      )
    )
  }
  getCities() {
    this.listOfCities$ = this.store.select(selectors.getCustomerLocationEntities).pipe(
      map((locations: ICustomerLocation[]) => {
        return mapToMultiselectOptions(
          locations.filter(l => l.city),
          l => l.city!,
          l => l.city!
        )
      })
    )
  }
  scrollToSection(id: string) {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }
}
